import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi } from '../api/authApi';
import { meApi } from '../api/meApi';
import { getToken, setToken, removeToken } from '../utils/storage';
import { provesTokenInvalid } from '../utils/unloadState';
import type { AuthUser, FeatureCatalogEntry, LoginRequest, RegisterRequest, User } from '../types/auth';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  // Feature 011
  accessibleFeatures: FeatureCatalogEntry[];
  mustChangePassword: boolean;
  refreshAccessibleFeatures: () => Promise<void>;
  clearMustChangePassword: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

function toAuthUser(user: User, accessibleFeatures: FeatureCatalogEntry[]): AuthUser {
  return { ...user, accessibleFeatures };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // /api/auth/me returns the flat me-shape including accessibleFeatures.
      const me = (await authApi.getCurrentUser()) as User & { accessibleFeatures?: FeatureCatalogEntry[] };
      setUser(toAuthUser(me, me.accessibleFeatures ?? []));
    } catch (error) {
      // Discard the token ONLY when the server said it is bad. This catch used to fire on any
      // failure at all, which meant a request that never got an answer logged the user out: on a
      // full page load /api/auth/me is in flight while the document may still be replaced (a dev
      // server reload, a fast second navigation, the user clicking another link), and the browser
      // cancels it. The bare catch read that cancellation as "your session is over", cleared the
      // credential and bounced to /login — losing the very token that would have made the next
      // attempt succeed. A transient network blip on load had the same effect.
      //
      // A non-401 failure now leaves the token in place. The user is still unauthenticated for this
      // render, because `user` was never set, so nothing is granted that shouldn't be — but the
      // credential survives for the retry.
      if (provesTokenInvalid(error)) {
        removeToken();
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (data: LoginRequest) => {
    const response = await authApi.login(data);
    setToken(response.token);
    setUser(toAuthUser(response.user, response.accessibleFeatures ?? []));
  };

  const register = async (data: RegisterRequest) => {
    const response = await authApi.register(data);
    setToken(response.token);
    setUser(toAuthUser(response.user, response.accessibleFeatures ?? []));
  };

  const logout = () => {
    // Feature 015 — record the session end server-side BEFORE clearing the token
    // so the request still carries the bearer header. Fire-and-forget.
    authApi.logout();
    removeToken();
    setUser(null);
  };

  const updateUser = (updatedUser: User) => {
    setUser(prev => prev ? toAuthUser(updatedUser, prev.accessibleFeatures) : null);
  };

  const refreshAccessibleFeatures = useCallback(async () => {
    if (!user) return;
    const features = await meApi.getAccessibleFeatures();
    setUser(prev => prev ? { ...prev, accessibleFeatures: features } : prev);
  }, [user]);

  const clearMustChangePassword = useCallback(() => {
    setUser(prev => prev ? { ...prev, mustChangePassword: false } : prev);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        updateUser,
        accessibleFeatures: user?.accessibleFeatures ?? [],
        mustChangePassword: user?.mustChangePassword ?? false,
        refreshAccessibleFeatures,
        clearMustChangePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
